import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Layers, TrendingUp, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { dataService } from '@rti/shared';
import {
  getPetchemData,
  getChemOrbisData,
  computeMovingAverage,
  getLatestMA,
  getLatestValue,
  type PetchemDataPoint,
  type ChemOrbisDataPoint,
} from '@/services/feedstockService';
import { isDateLabel, parseDateLiteral, formatDateLabel, inventoryChangeAxisLabel, isPastThreeYearMark } from '@/utils/chartAxisUtils';
import { getDefaultPreviousYearWindowFromLabels, sliceLabelsAndDatasets } from '@/utils/chartWindow';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { useRsColors } from '@/hooks/useRsColors';
import { useTheme } from '@/contexts/ThemeContext';
import FeedstockLineChart, { type AlignedDataset } from '@/components/charts/FeedstockLineChart';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';

type PolymerKey = 'PE' | 'PP' | 'PS' | 'PVC' | 'PET' | 'PC';
type DataSource = 'petchem' | 'contracts' | 'chemorbis' | 'petmaster';

interface SeriesConfig {
  label: string;
  dataKey: string;
  source: DataSource;
  color: string;
  unit?: string;
}

interface ChartConfig {
  title: string;
  unit: string;
  series: SeriesConfig[];
}

const COLORS = {
  green: 'rgba(132,204,22,0.95)',
  blue: 'rgba(59,130,246,0.95)',
  amber: 'rgba(245,158,11,0.95)',
  purple: 'rgba(168,85,247,0.95)',
  rose: 'rgba(244,63,94,0.95)',
  teal: 'rgba(16,185,129,0.95)',
};

const MA_PERIODS: Record<PolymerKey, number[]> = {
  PE: [30, 45, 60],
  PP: [30, 45, 60],
  PS: [30, 45, 60],
  PVC: [30, 45, 60],
  PET: [30, 45, 60],
  PC: [30, 45, 60],
};

const POLYMER_CHARTS: Record<PolymerKey, ChartConfig[]> = {
  PE: [
    {
      title: 'Crude Oil',
      unit: '$ / bbl',
      series: [
        { label: 'WTI', dataKey: 'wti_crude_oil', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Natural Gas',
      unit: '',
      series: [
        { label: 'Natural Gas', dataKey: 'nat_gas', source: 'petchem', color: COLORS.green, unit: '$ / MMBtu' },
      ],
    },
    {
      title: 'Ethane and Ethylene',
      unit: '',
      series: [
        { label: 'Ethane', dataKey: 'ethane', source: 'petchem', color: COLORS.blue, unit: '¢ / gal' },
        { label: 'Ethylene', dataKey: 'spot_ethylene', source: 'petchem', color: COLORS.green, unit: '¢ / lb' },
      ],
    },
    {
      title: 'Ethylene International',
      unit: '',
      series: [
        { label: 'Ethylene - CFR-SEA', dataKey: 'ethylene_cfr', source: 'chemorbis', color: COLORS.green, unit: '$ / mt' },
        { label: 'Ethylene - FD-NWE', dataKey: 'ethylene_fd', source: 'chemorbis', color: COLORS.blue, unit: '$ / mt' },
      ],
    },
  ],
  PP: [
    {
      title: 'Propylene China',
      unit: '$ / mt',
      series: [
        { label: 'Propylene CFR-China', dataKey: 'propylene_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Propylene NWE',
      unit: '$ / mt',
      series: [
        { label: 'Propylene FD-NWE', dataKey: 'propylene_fd', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Spot PGP',
      unit: '¢ / lb',
      series: [
        { label: 'Spot PGP', dataKey: 'spot_pgp', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Spot RGP',
      unit: '¢ / lb',
      series: [
        { label: 'Spot RGP', dataKey: 'spot_rgp', source: 'petchem', color: COLORS.green },
      ],
    },
  ],
  PS: [
    {
      title: 'WTI Crude Oil',
      unit: '$ / bbl',
      series: [
        { label: 'WTI Crude Oil', dataKey: 'wti_crude_oil', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Benzene',
      unit: '$ / gal',
      series: [
        { label: 'Benzene', dataKey: 'spot_benzene', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Benzene EU',
      unit: '$ / mt',
      series: [
        { label: 'Benzene EU (CIF)', dataKey: 'benzene_cif', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Butadiene',
      unit: '¢ / lb',
      series: [
        { label: 'Butadiene', dataKey: 'spot_butadiene', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Ethylene',
      unit: '¢ / lb',
      series: [
        { label: 'Ethylene', dataKey: 'spot_ethylene', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Ethylene EU',
      unit: '$ / mt',
      series: [
        { label: 'Ethylene FD-NWE', dataKey: 'ethylene_fd', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Ethylene SEA',
      unit: '$ / mt',
      series: [
        { label: 'Ethylene CFR-SEA', dataKey: 'ethylene_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Styrene',
      unit: '¢ / lb',
      series: [
        { label: 'Styrene', dataKey: 'spot_styrene', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Styrene CN',
      unit: '$ / mt',
      series: [
        { label: 'Styrene CFR-China', dataKey: 'styrene_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Styrene EU',
      unit: '$ / mt',
      series: [
        { label: 'Styrene FOB-NWE', dataKey: 'styrene_fob_nwe', source: 'chemorbis', color: COLORS.green },
      ],
    },
  ],
  PVC: [],
  PET: [
    {
      title: 'US PX',
      unit: '¢ / lb',
      series: [
        { label: 'US PX', dataKey: 'us_px', source: 'petmaster', color: COLORS.green },
      ],
    },
    {
      title: 'US PTA',
      unit: '¢ / lb',
      series: [
        { label: 'US PTA', dataKey: 'us_pta', source: 'petmaster', color: COLORS.green },
      ],
    },
    {
      title: 'Paraxylene CFR China',
      unit: '$ / mt',
      series: [
        { label: 'Paraxylene CFR China', dataKey: 'paraxylene_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'PTA CFR China',
      unit: '$ / mt',
      series: [
        { label: 'PTA CFR China', dataKey: 'pta_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Asian Contract Price MEG',
      unit: '¢ / lb',
      series: [
        { label: 'Asian Contract Price MEG', dataKey: 'acp_meg', source: 'petmaster', color: COLORS.green },
      ],
    },
    {
      title: 'Asia Spot MEG',
      unit: '¢ / lb',
      series: [
        { label: 'Asia Spot MEG', dataKey: 'asia_spot_meg', source: 'petmaster', color: COLORS.green },
      ],
    },
  ],
  PC: [],
};

const POLYMER_TAB_CHARTS: Partial<Record<PolymerKey, ChartConfig[]>> = {
  PP: [
    {
      title: 'Propylene China',
      unit: '$ / mt',
      series: [
        { label: 'Propylene CFR-China', dataKey: 'propylene_cfr', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Propylene NWE',
      unit: '$ / mt',
      series: [
        { label: 'Propylene FD-NWE', dataKey: 'propylene_fd', source: 'chemorbis', color: COLORS.green },
      ],
    },
    {
      title: 'Spot PGP',
      unit: '',
      series: [
        { label: 'Spot PGP', dataKey: 'spot_pgp', source: 'petchem', color: COLORS.green, unit: '¢ / lb' },
        { label: 'WTI Crude Oil', dataKey: 'wti_crude_oil', source: 'petchem', color: COLORS.blue, unit: '$ / bbl' },
      ],
    },
    {
      title: 'Spot RGP',
      unit: '¢ / lb',
      series: [
        { label: 'Spot RGP', dataKey: 'spot_rgp', source: 'petchem', color: COLORS.green },
      ],
    },
  ],
  PE: [
    {
      title: 'Crude Oil',
      unit: '$ / bbl',
      series: [
        { label: 'WTI', dataKey: 'wti_crude_oil', source: 'petchem', color: COLORS.green },
      ],
    },
    {
      title: 'Ethylene',
      unit: '',
      series: [
        { label: 'US Spot ETH', dataKey: 'spot_ethylene', source: 'petchem', color: COLORS.green, unit: '¢ / lb' },
        { label: 'ETH NWE', dataKey: 'ethylene_fd', source: 'chemorbis', color: COLORS.blue, unit: '$ / mt' },
        { label: 'ETH SEA', dataKey: 'ethylene_cfr', source: 'chemorbis', color: COLORS.purple, unit: '$ / mt' },
      ],
    },
    {
      title: 'Gas',
      unit: '$ / MMBtu',
      series: [
        { label: 'Natural Gas', dataKey: 'nat_gas', source: 'petchem', color: COLORS.green, unit: '$ / MMBtu' },
      ],
    },
  ],
  PS: [
    {
      title: 'Benzene',
      unit: '',
      series: [
        { label: 'Benzene', dataKey: 'spot_benzene', source: 'petchem', color: COLORS.green, unit: '$ / gal' },
        { label: 'Benzene EU', dataKey: 'benzene_cif', source: 'chemorbis', color: COLORS.blue, unit: '$ / mt' },
      ],
    },
    {
      title: 'Ethylene',
      unit: '',
      series: [
        { label: 'Ethylene', dataKey: 'spot_ethylene', source: 'petchem', color: COLORS.green, unit: '¢ / lb' },
        { label: 'Ethylene EU', dataKey: 'ethylene_fd', source: 'chemorbis', color: COLORS.blue, unit: '$ / mt' },
        { label: 'Ethylene SEA', dataKey: 'ethylene_cfr', source: 'chemorbis', color: COLORS.purple, unit: '$ / mt' },
      ],
    },
    {
      title: 'Styrene',
      unit: '',
      series: [
        { label: 'Styrene', dataKey: 'spot_styrene', source: 'petchem', color: COLORS.green, unit: '¢ / lb' },
        { label: 'Styrene CN', dataKey: 'styrene_cfr', source: 'chemorbis', color: COLORS.blue, unit: '$ / mt' },
        { label: 'Styrene EU', dataKey: 'styrene_fob_nwe', source: 'chemorbis', color: COLORS.purple, unit: '$ / mt' },
      ],
    },
  ],
};

const sortByDateAsc = <T extends { date: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

interface DataSources {
  petchem: any[];
  contracts: any[];
  chemorbis: any[];
  petmaster: any[];
}

const buildChartFromConfig = (
  config: ChartConfig,
  sources: DataSources,
): { labels: string[]; datasets: AlignedDataset[] } => {
  const perSeries = config.series.map((s) => {
    const rows = sources[s.source] || [];
    const sorted = sortByDateAsc(rows.filter((r: any) => r?.date));
    const map = new Map<string, number | null>();
    sorted.forEach((r: any) => {
      const date = String(r.date).slice(0, 10);
      const raw = r[s.dataKey];
      const num = raw === null || raw === undefined || raw === '' ? null : Number(raw);
      map.set(date, Number.isFinite(num as number) ? (num as number) : null);
    });
    return { series: s, map };
  });

  const allDates = new Set<string>();
  perSeries.forEach(({ map }) => map.forEach((_v, k) => allDates.add(k)));
  const labels = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const datasets: AlignedDataset[] = perSeries.map(({ series, map }) => ({
    label: series.label,
    color: series.color,
    data: labels.map((d) => map.get(d) ?? null),
  }));
  return { labels, datasets };
};

const extractSeriesValues = (
  series: { source: DataSource; dataKey: string },
  sources: DataSources,
): { labels: string[]; values: (number | null)[] } => {
  const rows = sources[series.source] || [];
  const sorted = sortByDateAsc(rows.filter((r: any) => r?.date));
  const labels = sorted.map((r: any) => String(r.date).slice(0, 10));
  const values = sorted.map((r: any) => {
    const raw = r[series.dataKey];
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });
  return { labels, values };
};

const KpiCard: React.FC<{
  label: string;
  maValue: number | null;
  currentValue: number | null;
  maPeriod: number;
  unit: string;
}> = ({ label, maValue, currentValue, maPeriod, unit }) => (
  <div className="bg-rs-jet/50 border border-rs-jet-light/50 rounded-lg p-3 min-w-[180px] flex-1">
    <p className="text-rs-off-white text-sm uppercase tracking-wider mb-1">{label}</p>
    <div className="flex items-baseline gap-2">
      <p className="text-rs-off-white font-semibold text-sm">
        {maValue !== null ? `$${maValue.toFixed(2)}` : '—'}
      </p>
      <span className="text-rs-off-white/80 text-sm">{maPeriod} day MA</span>
    </div>
    <div className="flex items-baseline gap-2 mt-0.5">
      <p className="text-rs-green text-sm font-medium">
        {currentValue !== null ? `$${currentValue.toFixed(2)}` : '—'}
      </p>
      <span className="text-rs-off-white/80 text-sm">Current</span>
    </div>
    <p className="text-rs-off-white/60 text-sm mt-0.5">{unit}</p>
  </div>
);

interface FeedstocksTabProps {
  polymer: PolymerKey;
  fullBleed?: boolean;
}

const FeedstocksTab: React.FC<FeedstocksTabProps> = ({ polymer, fullBleed = false }) => {
  const [petchemData, setPetchemData] = useState<PetchemDataPoint[] | null>(null);
  const [contractsData, setContractsData] = useState<any[] | null>(null);
  const [chemOrbisData, setChemOrbisData] = useState<ChemOrbisDataPoint[] | null>(null);
  const [petMasterData, setPetMasterData] = useState<any[] | null>(null);
  const hasFetched = useRef(false);
  const rs = useRsColors();

  const charts = POLYMER_CHARTS[polymer] || [];
  const tabCharts = POLYMER_TAB_CHARTS[polymer] || charts;
  const [activeChartTitle, setActiveChartTitle] = useState<string>(tabCharts[0]?.title || '');
  const [maPeriod, setMaPeriod] = useState<number>(30);
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({});
  const [hideOverlay, setHideOverlay] = useState<boolean>(false);
  // Below 750px, stack the feedstock charts in a single column instead of a 2-col grid.
  const [isSingleColumn, setIsSingleColumn] = useState(false);
  // Polymer-specific chevron navigation: PP (3 buttons at 750px, 2 buttons at 680px, 1 button at 560px), PET (3 buttons at 1360px, 2 buttons at 650px, 1 button at 450px), PE (1 button at 420px), PS (1 button at 420px)
  const [showChevrons, setShowChevrons] = useState(false);
  const [showChevrons2, setShowChevrons2] = useState(false);
  const [showChevrons3, setShowChevrons3] = useState(false);
  const [showChevronsPet2, setShowChevronsPet2] = useState(false);
  const [showChevronsPet3, setShowChevronsPet3] = useState(false);
  const [showChevronsPE, setShowChevronsPE] = useState(false);
  const [showChevrons420, setShowChevrons420] = useState(false);
  const [buttonIndex, setButtonIndex] = useState(0);
  const [buttonIndex2, setButtonIndex2] = useState(0);
  const [buttonIndex3, setButtonIndex3] = useState(0);
  const [buttonIndexPet2, setButtonIndexPet2] = useState(0);
  const [buttonIndexPet3, setButtonIndexPet3] = useState(0);
  const [buttonIndexPE, setButtonIndexPE] = useState(0);
  const [buttonIndex420, setButtonIndex420] = useState(0);
  const [isCentered, setIsCentered] = useState(false);
  const { isDark } = useTheme();

  // Get chevron config based on polymer
  const getChevronConfig = () => {
    if (polymer === 'PP') return { breakpoint: 750, displayCount: 3 };
    if (polymer === 'PET') return { breakpoint: 1360, displayCount: 3 };
    return { breakpoint: -1, displayCount: 0 };
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    setIsSingleColumn(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSingleColumn(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Chevron detection for polymer-specific breakpoints
  useEffect(() => {
    if (polymer !== 'PP' && polymer !== 'PET') return;
    const breakpoint = polymer === 'PP' ? 750 : 1360;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    setShowChevrons(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevrons(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Center buttons at 980px breakpoint (PP only)
  useEffect(() => {
    if (polymer !== 'PP') return;
    const mq = window.matchMedia('(max-width: 980px) and (min-width: 751px)');
    setIsCentered(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCentered(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Chevron 2-button mode at 680px breakpoint (PP only)
  useEffect(() => {
    if (polymer !== 'PP') return;
    const mq = window.matchMedia('(max-width: 680px) and (min-width: 561px)');
    setShowChevrons2(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevrons2(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndex2 with activeChartTitle for 2-button mode
  useEffect(() => {
    if (!showChevrons2) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndex2(currentIndex);
    }
  }, [activeChartTitle, showChevrons2, tabCharts]);

  // Chevron 1-button mode at 560px breakpoint (PP only)
  useEffect(() => {
    if (polymer !== 'PP') return;
    const mq = window.matchMedia('(max-width: 560px)');
    setShowChevrons3(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevrons3(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndex3 with activeChartTitle for 1-button mode
  useEffect(() => {
    if (!showChevrons3) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndex3(currentIndex);
    }
  }, [activeChartTitle, showChevrons3, tabCharts]);

  // Chevron 2-button mode at 650px breakpoint (PET only)
  useEffect(() => {
    if (polymer !== 'PET') return;
    const mq = window.matchMedia('(max-width: 650px)');
    setShowChevronsPet2(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevronsPet2(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndexPet2 with activeChartTitle for PET 2-button mode
  useEffect(() => {
    if (!showChevronsPet2) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndexPet2(currentIndex);
    }
  }, [activeChartTitle, showChevronsPet2, tabCharts]);

  // Chevron 1-button mode at 450px breakpoint (PET only)
  useEffect(() => {
    if (polymer !== 'PET') return;
    const mq = window.matchMedia('(max-width: 450px)');
    setShowChevronsPet3(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevronsPet3(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndexPet3 with activeChartTitle for PET 1-button mode
  useEffect(() => {
    if (!showChevronsPet3) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndexPet3(currentIndex);
    }
  }, [activeChartTitle, showChevronsPet3, tabCharts]);

  // Chevron 1-button mode at 420px breakpoint (PE only)
  useEffect(() => {
    if (polymer !== 'PE') return;
    const mq = window.matchMedia('(max-width: 420px)');
    setShowChevronsPE(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevronsPE(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndexPE with activeChartTitle for PE 1-button mode
  useEffect(() => {
    if (!showChevronsPE) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndexPE(currentIndex);
    }
  }, [activeChartTitle, showChevronsPE, tabCharts]);

  // Chevron 1-button mode at 420px breakpoint (PS only)
  useEffect(() => {
    if (polymer !== 'PS') return;
    const mq = window.matchMedia('(max-width: 420px)');
    setShowChevrons420(mq.matches);
    const handler = (e: MediaQueryListEvent) => setShowChevrons420(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  // Sync buttonIndex420 with activeChartTitle for PS 1-button mode
  useEffect(() => {
    if (!showChevrons420) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndex420(currentIndex);
    }
  }, [activeChartTitle, showChevrons420, tabCharts]);

  // Sync buttonIndex with activeChartTitle
  useEffect(() => {
    if (!showChevrons) return;
    const currentIndex = tabCharts.findIndex((c) => c.title === activeChartTitle);
    if (currentIndex >= 0) {
      setButtonIndex(currentIndex);
    }
  }, [activeChartTitle, showChevrons, tabCharts]);

  const currentChart = useMemo(
    () => tabCharts.find((c) => c.title === activeChartTitle) || tabCharts[0],
    [tabCharts, activeChartTitle],
  );

  useEffect(() => {
    if (!currentChart) return;
    const initial: Record<string, boolean> = {};
    currentChart.series.forEach((s) => {
      initial[s.dataKey] = true;
    });
    setVisibleSeries(initial);
    setHideOverlay(false);
  }, [currentChart?.title]);

  const usedSources = useMemo(() => {
    const set = new Set<DataSource>();
    charts.forEach((c) => c.series.forEach((s) => set.add(s.source)));
    tabCharts.forEach((c) => c.series.forEach((s) => set.add(s.source)));
    return set;
  }, [charts, tabCharts]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    if (usedSources.has('petchem')) {
      getPetchemData()
        .then((d) => setPetchemData(d || []))
        .catch(() => setPetchemData([]));
    } else {
      setPetchemData([]);
    }

    if (usedSources.has('contracts')) {
      dataService.getContractsData()
        .then((d: any) => setContractsData(Array.isArray(d) ? d : []))
        .catch(() => setContractsData([]));
    } else {
      setContractsData([]);
    }

    if (usedSources.has('chemorbis')) {
      getChemOrbisData()
        .then((d) => setChemOrbisData(d || []))
        .catch(() => setChemOrbisData([]));
    } else {
      setChemOrbisData([]);
    }

    if (usedSources.has('petmaster')) {
      dataService.getPetMasterPricingData()
        .then((d: any) => setPetMasterData(Array.isArray(d) ? d : []))
        .catch(() => setPetMasterData([]));
    } else {
      setPetMasterData([]);
    }
  }, [usedSources]);

  const sources: DataSources | null = useMemo(() => {
    if (!petchemData || !contractsData || !chemOrbisData || !petMasterData) return null;
    return {
      petchem: petchemData,
      contracts: contractsData,
      chemorbis: chemOrbisData,
      petmaster: petMasterData,
    };
  }, [petchemData, contractsData, chemOrbisData, petMasterData]);

  const builtCharts = useMemo(() => {
    if (!sources) return null;
    return charts.map((c) => ({ config: c, ...buildChartFromConfig(c, sources) }));
  }, [charts, sources]);

  const activeSeries = currentChart?.series || [];

  const kpiData = useMemo(() => {
    if (!sources || !currentChart) return [];
    return currentChart.series.map((s) => {
      const { values } = extractSeriesValues(s, sources);
      return {
        label: s.label,
        dataKey: s.dataKey,
        color: s.color,
        unit: s.unit || currentChart.unit || '',
        maValue: getLatestMA(values, maPeriod),
        currentValue: getLatestValue(values),
      };
    });
  }, [currentChart, sources, maPeriod]);

  const mainChart = useMemo(() => {
    if (!sources || !currentChart) return null;
    const renderedSeries = currentChart.series.filter((s) => visibleSeries[s.dataKey]);

    const perSeries = renderedSeries.map((s) => {
      const { labels, values } = extractSeriesValues(s, sources);
      const map = new Map<string, number | null>();
      labels.forEach((d, i) => map.set(d, values[i]));
      return { series: s, map };
    });

    const allDates = new Set<string>();
    perSeries.forEach(({ map }) => map.forEach((_v, k) => allDates.add(k)));
    const labels = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const datasets: any[] = [];
    perSeries.forEach(({ series, map }) => {
      const data = labels.map((d) => map.get(d) ?? null);
      datasets.push({
        label: series.label,
        data,
        borderColor: series.color,
        backgroundColor: 'transparent',
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.3,
        spanGaps: true,
        yAxisID: 'y',
      });
      if (!hideOverlay) {
        const ma = computeMovingAverage(data, maPeriod);
        datasets.push({
          label: `${series.label} ${maPeriod}d MA`,
          data: ma,
          borderColor: series.color,
          backgroundColor: 'transparent',
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [6, 3],
          tension: 0.3,
          spanGaps: true,
          yAxisID: 'y',
        });
      }
    });

    return { labels, datasets };
  }, [sources, currentChart, visibleSeries, hideOverlay, maPeriod]);

  const mainDefaultWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(mainChart?.labels || []),
    [mainChart?.labels],
  );

  const {
    startIndex: mainStartIndex,
    endIndex: mainEndIndex,
    setWindow: setMainWindow,
    hasWindowing: hasMainWindowing,
  } = useChartWindow(mainChart?.labels.length || 0, {
    defaultStartIndex: mainDefaultWindow.startIndex,
    defaultEndIndex: mainDefaultWindow.endIndex,
  });

  const visibleMainChart = useMemo(() => {
    if (!mainChart) return null;
    return sliceLabelsAndDatasets(mainChart.labels, mainChart.datasets, mainStartIndex, mainEndIndex);
  }, [mainChart, mainStartIndex, mainEndIndex]);

  const mainChartOptions: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        mode: 'index' as const,
        intersect: false,
        filter: (item: any) => item.raw !== null,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            const raw = items[0].label;
            if (isDateLabel(raw)) return formatDateLabel(parseDateLiteral(raw));
            return raw;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        bounds: 'data' as const,
        time: {
          unit: 'month' as const,
          displayFormats: { month: 'MMM yyyy' },
          tooltipFormat: 'MMM d, yyyy',
        },
        afterBuildTicks: (axis: any) => {
          const labels: string[] = visibleMainChart?.labels || [];
          if (!labels.length) return;
          const first = new Date(labels[0]);
          const last = new Date(labels[labels.length - 1]);
          if (isNaN(first.getTime()) || isNaN(last.getTime())) return;
          // Window-based (this chart has a slider): within 3yr → every 6 months (Jan/Jul);
          // once the window is dragged past the 3-year mark → yearly (January).
          const yearOnly = isPastThreeYearMark(labels);
          let cursor = new Date(last.getFullYear(), last.getMonth(), 1);
          const ticks: { value: number }[] = [];
          while (cursor.getTime() >= first.getTime()) {
            const m = cursor.getMonth();
            const keep = yearOnly ? m === 0 : m % 6 === 0;
            if (keep) ticks.push({ value: cursor.getTime() });
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
          }
          if (ticks.length > 0) {
            ticks.reverse();
            axis.ticks = ticks;
          }
        },
        grid: { color: rs.gridLine, drawTicks: false },
        border: { display: false },
        ticks: {
          color: rs.offWhite,
          font: { size: 12 },
          maxRotation: 0,
          autoSkip: false,
          callback: (value: any) => {
            const d = new Date(value);
            if (isNaN(d.getTime())) return '';
            // <3yr window → MMM yyyy; dragged past 3yr → year only
            return inventoryChangeAxisLabel(d, isPastThreeYearMark(visibleMainChart?.labels || []));
          },
        },
      },
      y: {
        ticks: {
          color: rs.offWhite,
          font: { size: 12 },
          callback: (v: any) => Number(v).toFixed(2),
        },
        grid: { color: rs.gridLine },
        title: {
          display: !!currentChart?.unit,
          text: currentChart?.unit || '',
          color: rs.offWhite,
          font: { size: 12 },
        },
      },
    },
  }), [rs, currentChart?.unit, visibleMainChart?.labels]);

  const hasMainChart = !!visibleMainChart && visibleMainChart.labels.length > 0;

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs]);

  if (!builtCharts) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin w-6 h-6 text-rs-off-white/30" />
      </div>
    );
  }

  if (charts.length === 0) {
    return (
      <div className="bg-rs-off-black/60 border border-rs-jet-light/50 rounded-xl p-6 text-rs-off-white/40 text-sm">
        No feedstock charts configured for {polymer}.
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${fullBleed && isSingleColumn ? 'p-0 -mx-6' : 'p-4'}`}>
      {tabCharts.length > 0 && currentChart && (
        <>
          <div className={`flex flex-wrap items-center ${showChevrons420 || showChevronsPE || showChevrons3 || showChevrons2 || showChevronsPet3 || showChevronsPet2 || showChevrons ? 'flex-col gap-4 justify-center w-full' : isCentered ? 'flex-col gap-4 justify-center w-full' : isSingleColumn ? 'flex-col gap-4 justify-center w-full' : 'justify-between gap-3'}`}>
            {showChevronsPE && polymer === 'PE' ? (() => {
              const displayCount = 1;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndexPE, buttonIndexPE + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndexPE - displayCount);
                      setButtonIndexPE(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPE === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPE === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndexPE + displayCount);
                      setButtonIndexPE(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPE >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPE >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevrons420 && polymer === 'PS' ? (() => {
              const displayCount = 1;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndex420, buttonIndex420 + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndex420 - displayCount);
                      setButtonIndex420(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex420 === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex420 === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndex420 + displayCount);
                      setButtonIndex420(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex420 >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex420 >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevrons3 ? (() => {
              const displayCount = 1;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndex3, buttonIndex3 + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndex3 - displayCount);
                      setButtonIndex3(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex3 === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex3 === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndex3 + displayCount);
                      setButtonIndex3(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex3 >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex3 >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevrons2 ? (() => {
              const displayCount = 2;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndex2, buttonIndex2 + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndex2 - displayCount);
                      setButtonIndex2(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex2 === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex2 === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndex2 + displayCount);
                      setButtonIndex2(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex2 >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex2 >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevronsPet3 && polymer === 'PET' ? (() => {
              const displayCount = 1;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndexPet3, buttonIndexPet3 + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndexPet3 - displayCount);
                      setButtonIndexPet3(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPet3 === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPet3 === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndexPet3 + displayCount);
                      setButtonIndexPet3(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPet3 >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPet3 >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevronsPet2 && polymer === 'PET' ? (() => {
              const displayCount = 2;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndexPet2, buttonIndexPet2 + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndexPet2 - displayCount);
                      setButtonIndexPet2(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPet2 === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPet2 === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndexPet2 + displayCount);
                      setButtonIndexPet2(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndexPet2 >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndexPet2 >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : showChevrons ? (() => {
              const config = getChevronConfig();
              const displayCount = config.displayCount;
              const maxStartIndex = Math.max(0, tabCharts.length - displayCount);
              const visibleCharts = tabCharts.slice(buttonIndex, buttonIndex + displayCount);

              return (
                <div className={`flex items-center gap-2 justify-center ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, buttonIndex - displayCount);
                      setButtonIndex(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex === 0}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex === 0
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>
                  <div className="flex gap-1">
                    {visibleCharts.map((c) => (
                      <button
                        key={c.title}
                        onClick={() => setActiveChartTitle(c.title)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeChartTitle === c.title
                            ? 'bg-rs-green text-black'
                            : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                        }`}
                      >
                        <Layers size={13} className="flex-shrink-0" />
                        {c.title}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(maxStartIndex, buttonIndex + displayCount);
                      setButtonIndex(newIndex);
                      setActiveChartTitle(tabCharts[newIndex]?.title || '');
                    }}
                    disabled={buttonIndex >= maxStartIndex}
                    className={`p-1 rounded transition-colors ${
                      buttonIndex >= maxStartIndex
                        ? 'text-rs-off-white/20 cursor-not-allowed'
                        : isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-jet'
                    }`}
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })() : (
              <div className={`flex gap-1 ${isSingleColumn ? 'justify-center' : ''} ${fullBleed && isSingleColumn ? 'px-4 w-full' : ''}`}>
                {tabCharts.map((c) => (
                  <button
                    key={c.title}
                    onClick={() => setActiveChartTitle(c.title)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeChartTitle === c.title
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    <Layers size={13} className="flex-shrink-0" />
                    {c.title}
                  </button>
                ))}
              </div>
            )}

            <div className={`flex items-center gap-2 ${showChevrons420 || showChevronsPE || showChevronsPet3 || showChevronsPet2 || isCentered || isSingleColumn ? 'justify-center' : ''} ${fullBleed && isSingleColumn ? 'px-4' : ''}`}>
              <span className="flex items-center gap-1.5 text-rs-off-white/50 text-sm">
                <TrendingUp size={13} className="opacity-50" />
                MA Period
              </span>
              {MA_PERIODS[polymer].map((p) => (
                <button
                  key={p}
                  onClick={() => setMaPeriod(p)}
                  className={`w-10 h-8 rounded text-sm font-medium transition-colors ${
                    maPeriod === p
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className={`flex flex-wrap gap-3 ${isSingleColumn ? 'justify-center' : ''} ${fullBleed && isSingleColumn ? 'px-4' : ''}`}>
            {kpiData.map((kpi) => (
              <KpiCard
                key={kpi.dataKey}
                label={`${kpi.label} ${maPeriod}D MA`}
                maValue={kpi.maValue}
                currentValue={kpi.currentValue}
                maPeriod={maPeriod}
                unit={kpi.unit}
              />
            ))}
          </div>

          <div className={`flex flex-wrap items-center gap-2 ${fullBleed && isSingleColumn ? 'px-4' : ''}`}>
            <div className="flex flex-wrap gap-1.5">
              {activeSeries.map((s) => (
                <label
                  key={s.dataKey}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded cursor-pointer text-sm font-medium transition-colors ${
                    visibleSeries[s.dataKey]
                      ? 'bg-rs-jet-light text-rs-off-white'
                      : 'bg-rs-jet/50 text-rs-off-white/40'
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-sm border flex-shrink-0"
                    style={{
                      backgroundColor: visibleSeries[s.dataKey] ? s.color : 'transparent',
                      borderColor: s.color,
                    }}
                  />
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!visibleSeries[s.dataKey]}
                    onChange={() =>
                      setVisibleSeries((prev) => ({ ...prev, [s.dataKey]: !prev[s.dataKey] }))
                    }
                  />
                  {s.label}
                </label>
              ))}
            </div>
            <div className="ml-auto flex gap-1.5">
              <button
                onClick={() => setHideOverlay(!hideOverlay)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  hideOverlay
                    ? 'bg-rs-green text-black'
                    : 'bg-rs-jet/50 text-rs-off-white/40 hover:bg-rs-jet-light'
                }`}
              >
                {hideOverlay ? <Eye size={13} /> : <EyeOff size={13} />}
                Hide
              </button>
            </div>
          </div>

          <div className={`bg-rs-off-black/60 border border-rs-jet-light/50 rounded-xl ${fullBleed && isSingleColumn ? 'p-2' : 'p-4'}`}>
            {hasMainChart ? (
              <div className="h-[500px]">
                <Line key={rs.offWhite} data={visibleMainChart!} options={mainChartOptions} plugins={[hoverLinePlugin]} />
              </div>
            ) : (
              <div className="h-[500px] flex items-center justify-center text-rs-off-white/40 text-sm">
                No data available
              </div>
            )}
            {hasMainWindowing && mainChart && (
              <ChartRangeSlider
                totalPoints={mainChart.labels.length}
                startIndex={mainStartIndex}
                endIndex={mainEndIndex}
                onChange={setMainWindow}
              />
            )}
          </div>
        </>
      )}

      {polymer === 'PP' ? (
        <div className={isSingleColumn ? 'flex flex-col gap-4' : 'grid grid-cols-2 gap-4'}>
          {builtCharts.map((c) => (
            <FeedstockLineChart
              key={c.config.title}
              title={c.config.title}
              unit={c.config.unit}
              labels={c.labels}
              alignedChartDatasets={c.datasets}
            />
          ))}
        </div>
      ) : (
        <div className={isSingleColumn ? 'flex flex-col gap-4' : 'grid grid-cols-2 gap-4'}>
          {builtCharts.map((c) => (
            <FeedstockLineChart
              key={c.config.title}
              title={c.config.title}
              unit={c.config.unit}
              labels={c.labels}
              alignedChartDatasets={c.datasets}
            />
          ))}
        </div>
      )}

      <p data-source-tag className={`text-xs text-rs-highlight-gray mt-5 ${fullBleed && isSingleColumn ? 'px-4' : ''}`}>
        Source: RTI PetChem / Chemorbis
      </p>
    </div>
  );
};

export default FeedstocksTab;
